import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../../utils/auth';
import { PRODUCT_MASTER_PRIVILEGES } from '../../../../utils/productMasterPrivileges';
import Sidebar from '../../../../components/Sidebar/sidebar';
import Header from '../../../../components/Header';
import Pagination from '../../../../components/Pagination/Pagination';
import './insurerMaster.css';
import api from '../../../../utils/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
    ArchiveRestore,
    Ban,
    Building2,
    Download,
    Edit,
    FileSpreadsheet,
    Image as ImageIcon,
    Plus,
    RefreshCw,
    Search,
    ShieldCheck,
    Tags,
    Upload,
    X
} from 'lucide-react';
import { API_BASE_URL } from '../../../../utils/apiBase';
import { toast } from '../../../../components/notifications/notificationService';

const emptyForm = {
    insurerName: '',
    shortName: '',
    categoryId: '',
    address: '',
    gstNumber: '',
    logo: null
};

const InsuranceMaster = () => {
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const insurerPrivileges = PRODUCT_MASTER_PRIVILEGES.insurer;
    const [collapsed, setCollapsed] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [insurers, setInsurers] = useState([]);
    const [inactiveInsurers, setInactiveInsurers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState(emptyForm);
    const [preview, setPreview] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [insurerToDeactivate, setInsurerToDeactivate] = useState(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [activeSearchText, setActiveSearchText] = useState('');
    const [activeCategoryFilter, setActiveCategoryFilter] = useState('');
    const [searchText, setSearchText] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [activePage, setActivePage] = useState(1);
    const [activeItemsPerPage, setActiveItemsPerPage] = useState(10);
    const [bulkErrors, setBulkErrors] = useState([]);
    const [showBulkErrorPopup, setShowBulkErrorPopup] = useState(false);
    useEffect(() => {
        const auth = isAuthenticated();
        setAuthenticated(auth);
        setCheckingAuth(false);
    }, []);

    const showNotification = useCallback((message, type) => {
        (toast[type] || toast.info)(message);
    }, []);

    const fetchActiveInsurers = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/insurers`);
            setInsurers(res.data.insurers || []);
        } catch (err) {
            console.error(err);
            showNotification('Error fetching insurers', 'error');
        }
    }, [showNotification]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/categories`);
            setCategories(res.data || []);
        } catch (err) {
            console.error(err);
            showNotification('Error fetching categories', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        if (!authenticated) return;
        fetchActiveInsurers();
        fetchCategories();
    }, [authenticated, fetchActiveInsurers, fetchCategories]);

    const getCategoryName = useCallback((insurer) => {
        const cat = insurer.categoryId;
        if (cat && typeof cat === 'object') return cat.categoryName || '-';
        if (typeof cat === 'string') {
            const found = categories.find((c) => c._id === cat);
            return found ? found.categoryName : '-';
        }
        return '-';
    }, [categories]);

    const filteredActive = useMemo(() => {
        const text = activeSearchText.trim().toLowerCase();
        return insurers.filter((item) => {
            const searchableText = [
                item.insurerName,
                item.shortName,
                item.address,
                item.gstNumber,
                getCategoryName(item)
            ].join(' ').toLowerCase();

            const matchesText = !text || searchableText.includes(text);
            const matchesCategory =
                !activeCategoryFilter ||
                (item.categoryId &&
                    (typeof item.categoryId === 'object'
                        ? item.categoryId._id === activeCategoryFilter
                        : item.categoryId === activeCategoryFilter));

            return matchesText && matchesCategory;
        });
    }, [insurers, activeSearchText, activeCategoryFilter, getCategoryName]);

    const activeLast = activePage * activeItemsPerPage;
    const activeFirst = activeLast - activeItemsPerPage;
    const activeItems = filteredActive.slice(activeFirst, activeLast);

    const filteredInactive = inactiveInsurers
        .filter((item) => {
            const text = searchText.toLowerCase();
            const nameMatch =
                item.insurerName.toLowerCase().includes(text) ||
                item.shortName.toLowerCase().includes(text);

            const catFilterOk =
                !filterCategory ||
                (item.categoryId &&
                    (typeof item.categoryId === 'object'
                        ? item.categoryId._id === filterCategory
                        : item.categoryId === filterCategory));

            return nameMatch && catFilterOk;
        })
        .sort((a, b) => {
            if (!sortField) return 0;

            const valA = sortField === 'categoryName'
                ? getCategoryName(a)
                : (a[sortField] || '').toString();
            const valB = sortField === 'categoryName'
                ? getCategoryName(b)
                : (b[sortField] || '').toString();

            return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        });

    const summaryCards = [
        { label: 'Active Insurers', value: insurers.length, note: 'Available in workflows', icon: ShieldCheck, tone: 'is-blue' },
        { label: 'Categories', value: categories.length, note: 'Mapped categories', icon: Tags, tone: 'is-green' },
        { label: 'With Logos', value: insurers.filter((item) => item.logo).length, note: 'Brand assets uploaded', icon: ImageIcon, tone: 'is-amber' },
        { label: 'GST Available', value: insurers.filter((item) => item.gstNumber).length, note: 'Compliance ready', icon: FileSpreadsheet, tone: 'is-teal' },
        { label: 'Inactive Insurers', value: inactiveInsurers.length, note: 'Loaded from inactive list', icon: ArchiveRestore, tone: 'is-indigo' }
    ];

    const getExportRows = (rows = filteredActive) =>
        rows.map((insurer, index) => ({
            'S.No': index + 1,
            'Insurer Name': insurer.insurerName || '',
            'Short Name': insurer.shortName || '',
            Category: getCategoryName(insurer),
            Address: insurer.address || '',
            'GST Number': insurer.gstNumber || '',
            'Logo Status': insurer.logo ? 'Uploaded' : 'Pending'
        }));

    const exportInsurersExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurer Master');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'insurer-master.xlsx');
    };

    const exportInsurersCsv = () => {
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'insurer-master.csv');
    };

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const exportInsurersPdf = () => {
        const rows = getExportRows();
        const printWindow = window.open('', '_blank', 'noopener,noreferrer');
        if (!printWindow) {
            showNotification('Please allow popups to export PDF.', 'error');
            return;
        }

        const tableRows = rows.map((row) => `
            <tr>
                <td>${escapeHtml(row['S.No'])}</td>
                <td>${escapeHtml(row['Insurer Name'])}</td>
                <td>${escapeHtml(row['Short Name'])}</td>
                <td>${escapeHtml(row.Category)}</td>
                <td>${escapeHtml(row.Address)}</td>
                <td>${escapeHtml(row['GST Number'])}</td>
                <td>${escapeHtml(row['Logo Status'])}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <!doctype html>
            <html>
              <head>
                <title>Insurer Master Export</title>
                <style>
                  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
                  h1 { margin: 0; font-size: 22px; }
                  p { margin: 6px 0 18px; color: #667085; font-size: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; }
                  th, td { border: 1px solid #d7e0ea; padding: 8px; text-align: left; vertical-align: top; }
                  th { background: #f1f5f9; text-transform: uppercase; font-size: 10px; }
                  tr:nth-child(even) td { background: #fbfdff; }
                  @media print { body { margin: 12mm; } }
                </style>
              </head>
              <body>
                <h1>Insurer Master</h1>
                <p>${rows.length} insurer record(s) exported on ${new Date().toLocaleString('en-IN')}</p>
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Insurer Name</th>
                      <th>Short Name</th>
                      <th>Category</th>
                      <th>Address</th>
                      <th>GST Number</th>
                      <th>Logo Status</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows || '<tr><td colspan="7">No insurer records found.</td></tr>'}</tbody>
                </table>
                <script>
                  window.onload = function () {
                    window.print();
                  };
                </script>
              </body>
            </html>
        `);
        printWindow.document.close();
    };

    const openInactivePopup = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/insurers/inactive/list`);
            setInactiveInsurers(res.data || []);
            setSearchText('');
            setFilterCategory('');
            setSortField('');
            setSortOrder('asc');
            setShowInactivePopup(true);
        } catch (err) {
            console.error(err);
            showNotification('Error fetching inactive insurers', 'error');
        }
    };

    const handleRestore = async (id) => {
        try {
            await api.put(`${API_BASE_URL}/api/insurers/activate/${id}`);
            const restored = inactiveInsurers.find((i) => i._id === id);
            if (restored) {
                setInsurers((prev) => [...prev, restored]);
                setInactiveInsurers((prev) => prev.filter((i) => i._id !== id));
            }
            showNotification('Insurer restored successfully', 'success');
        } catch (err) {
            console.error(err);
            showNotification('Error restoring insurer', 'error');
        }
    };

    const restoreAll = async () => {
        try {
            for (const item of inactiveInsurers) {
                await api.put(`${API_BASE_URL}/api/insurers/activate/${item._id}`);
            }
            setInsurers((prev) => [...prev, ...inactiveInsurers]);
            setInactiveInsurers([]);
            showNotification('All insurers restored successfully', 'success');
        } catch (err) {
            console.error(err);
            showNotification('Error restoring all insurers', 'error');
        }
    };

    const handleInsurerBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await api.post(
                `${API_BASE_URL}/api/insurers/bulk-upload`,
                uploadData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            const { inserted, failed, errors } = res.data;

            if (inserted > 0 && failed === 0) {
                showNotification(`Bulk upload completed successfully. ${inserted} insurers added.`, 'success');
            } else if (inserted > 0 && failed > 0) {
                showNotification(`Bulk upload completed with warnings. ${inserted} added, ${failed} failed.`, 'warning');
                setBulkErrors(errors);
                setShowBulkErrorPopup(true);
            } else {
                showNotification('Bulk upload failed. No insurers were added.', 'error');
            }

            await fetchActiveInsurers();
        } catch (err) {
            showNotification(err.response?.data?.message || 'Bulk upload failed', 'error');
        }

        e.target.value = null;
    };

    const downloadBulkTemplate = async () => {
        try {
            const response = await api.get(`${API_BASE_URL}/api/insurers/bulk-template`, {
                responseType: 'blob'
            });
            saveAs(response.data, 'Insurer_Bulk_Upload_Template.xlsx');
        } catch (error) {
            showNotification('Unable to download insurer template', 'error');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, logo: file }));
            setPreview(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setPreview(null);
        setEditMode(false);
        setCurrentId(null);
        setShowPopup(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = new FormData();
            Object.entries(formData).forEach(([key, val]) => {
                if (val !== null && val !== '') payload.append(key, val);
            });

            let response;

            if (editMode) {
                response = await api.put(
                    `${API_BASE_URL}/api/insurers/${currentId}`,
                    payload,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                setInsurers((prev) =>
                    prev.map((i) => (i._id === currentId ? response.data.insurer : i))
                );
                showNotification('Insurer updated successfully', 'success');
            } else {
                response = await api.post(
                    `${API_BASE_URL}/api/insurers`,
                    payload,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                setInsurers((prev) => [...prev, response.data.insurer]);
                showNotification('Insurer added successfully', 'success');
            }

            resetForm();
        } catch (err) {
            console.error(err);
            showNotification('Error saving insurer', 'error');
        }
    };

    const handleEdit = (insurer) => {
        const cat = insurer.categoryId;
        const resolvedCategoryId =
            cat && typeof cat === 'object'
                ? cat._id
                : typeof cat === 'string'
                    ? cat
                    : '';

        setFormData({
            insurerName: insurer.insurerName,
            shortName: insurer.shortName,
            categoryId: resolvedCategoryId,
            address: insurer.address || '',
            gstNumber: insurer.gstNumber || '',
            logo: null
        });

        setPreview(insurer.logo ? `${API_BASE_URL}/${insurer.logo}` : null);
        setEditMode(true);
        setCurrentId(insurer._id);
        setShowPopup(true);
    };

    const confirmDeactivate = async () => {
        try {
            await api.put(`${API_BASE_URL}/api/insurers/deactivate/${insurerToDeactivate}`);
            setInsurers((prev) => prev.filter((i) => i._id !== insurerToDeactivate));
            showNotification('Insurer deactivated successfully', 'success');
        } catch (err) {
            console.error(err);
            showNotification('Error deactivating insurer', 'error');
        } finally {
            setShowDeleteConfirm(false);
            setInsurerToDeactivate(null);
        }
    };

    const cancelDeactivate = () => {
        setShowDeleteConfirm(false);
        setInsurerToDeactivate(null);
    };

    if (checkingAuth) {
        return <div className="loading-screen">Loading...</div>;
    }

    if (!authenticated) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="insurer-master-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`insurer-master-content-shell ${collapsed ? 'collapsed' : ''}`}>
                <div className="insurer-master-sticky-header">
                    <Header />
                </div>

                <main className="insurer-master-scroll-area">
                    <section className="insurer-master-content">
                        <div className="insurer-page-hero">
                            <div>
                                <span className="insurer-page-eyebrow">System Settings / Product Master</span>
                                <h1>Insurer Master</h1>
                                <p>Manage insurance companies, categories, GST details, logos, and activation status from one workspace.</p>
                            </div>
                            <div className="insurer-hero-stats">
                                <span>Active Insurers</span>
                                <strong>{insurers.length}</strong>
                                <small>{filteredActive.length} matching current filters</small>
                            </div>
                        </div>

                        <div className="insurer-summary-grid">
                            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                                <article className={`insurer-summary-card ${tone}`} key={label}>
                                    <Icon size={22} />
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                    <small>{note}</small>
                                </article>
                            ))}
                        </div>

                        <section className="insurer-filter-card">
                            <div className="insurer-filter-header">
                                <div>
                                    <h2>Search & Actions</h2>
                                    <p>Search by insurer name, short name, category, address, or GST number.</p>
                                </div>
                                <div className="insurer-result-pill">{filteredActive.length} records</div>
                            </div>

                            <div className="insurer-filter-grid">
                                <label className="insurer-search-field">
                                    <span>Search</span>
                                    <div>
                                        <Search size={17} />
                                        <input
                                            type="search"
                                            placeholder="Insurer, short name, GST, category"
                                            value={activeSearchText}
                                            onChange={(e) => {
                                                setActiveSearchText(e.target.value);
                                                setActivePage(1);
                                            }}
                                        />
                                    </div>
                                </label>

                                <label>
                                    <span>Category</span>
                                    <select
                                        value={activeCategoryFilter}
                                        onChange={(e) => {
                                            setActiveCategoryFilter(e.target.value);
                                            setActivePage(1);
                                        }}
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map((cat) => (
                                            <option key={cat._id} value={cat._id}>
                                                {cat.categoryName}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="insurer-action-row">
                                {hasPrivilege(insurerPrivileges.add) && <button
                                    type="button"
                                    className="insurer-btn insurer-btn-primary"
                                    onClick={() => {
                                        resetForm();
                                        setShowPopup(true);
                                    }}
                                >
                                    <Plus size={16} /> Add Insurer
                                </button>}
                                {hasPrivilege(insurerPrivileges.bulkUpload) && <button
                                    type="button"
                                    className="insurer-btn insurer-btn-success"
                                    onClick={() => document.getElementById('insurerBulkInput').click()}
                                >
                                    <Upload size={16} /> Bulk Upload
                                </button>}
                                {hasPrivilege(insurerPrivileges.bulkUpload) && <button
                                    type="button"
                                    className="insurer-btn insurer-btn-ghost"
                                    onClick={downloadBulkTemplate}
                                >
                                    <Download size={16} /> Download Template
                                </button>}
                                {hasPrivilege(insurerPrivileges.viewInactive) && <button type="button" className="insurer-btn insurer-btn-light" onClick={openInactivePopup}>
                                    <ArchiveRestore size={16} /> Inactive List
                                </button>}
                                {hasPrivilege(insurerPrivileges.download) && <div className="insurer-export-menu">
                                    <button
                                        type="button"
                                        className="insurer-btn insurer-btn-export"
                                        onClick={() => setShowExportMenu((prev) => !prev)}
                                    >
                                        <Download size={16} /> Download
                                    </button>
                                    {showExportMenu && (
                                        <div className="insurer-export-dropdown">
                                            <button type="button" onClick={() => { exportInsurersCsv(); setShowExportMenu(false); }}>
                                                CSV
                                            </button>
                                            <button type="button" onClick={() => { exportInsurersExcel(); setShowExportMenu(false); }}>
                                                Excel
                                            </button>
                                            <button type="button" onClick={() => { exportInsurersPdf(); setShowExportMenu(false); }}>
                                                PDF
                                            </button>
                                        </div>
                                    )}
                                </div>}
                                <button
                                    type="button"
                                    className="insurer-btn insurer-btn-ghost"
                                    onClick={() => {
                                        setActiveSearchText('');
                                        setActiveCategoryFilter('');
                                        setActivePage(1);
                                    }}
                                >
                                    <RefreshCw size={16} /> Reset
                                </button>
                            </div>

                            <input
                                type="file"
                                id="insurerBulkInput"
                                accept=".xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={handleInsurerBulkUpload}
                            />
                        </section>

                        <section className="insurer-table-card">
                            <div className="insurer-table-toolbar">
                                <div>
                                    <h2>Active Insurers</h2>
                                    <p>Sticky insurer column with fast actions and compliance details.</p>
                                </div>
                            </div>

                            <div className="insurer-table-scroll">
                                <table className="insurer-table">
                                    <thead>
                                        <tr>
                                            <th className="insurer-sticky-name">Insurer</th>
                                            <th>Short Name</th>
                                            <th>Category</th>
                                            <th>Address</th>
                                            <th>GST Number</th>
                                            <th>Logo</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeItems.length > 0 ? (
                                            activeItems.map((insurer) => (
                                                <tr key={insurer._id}>
                                                    <td className="insurer-sticky-name" data-label="Insurer">
                                                        <div className="insurer-name-cell">
                                                            <span className="insurer-avatar">
                                                                {insurer.logo ? (
                                                                    <img src={`${API_BASE_URL}/${insurer.logo}`} alt={insurer.insurerName} />
                                                                ) : (
                                                                    <Building2 size={17} />
                                                                )}
                                                            </span>
                                                            <span>
                                                                <strong>{insurer.insurerName}</strong>
                                                                <small>{insurer.shortName || 'No short name'}</small>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Short Name">{insurer.shortName || '-'}</td>
                                                    <td data-label="Category"><span className="insurer-chip">{getCategoryName(insurer)}</span></td>
                                                    <td data-label="Address">
                                                        <span className="insurer-address" title={insurer.address || 'No address available'}>
                                                            {insurer.address || '-'}
                                                        </span>
                                                    </td>
                                                    <td data-label="GST Number">{insurer.gstNumber || '-'}</td>
                                                    <td data-label="Logo">
                                                        <span className={`insurer-logo-status ${insurer.logo ? 'is-ready' : ''}`}>
                                                            <ImageIcon size={14} /> {insurer.logo ? 'Uploaded' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td data-label="Actions">
                                                        <div className="action-icons">
                                                            {hasPrivilege(insurerPrivileges.edit) && <button className="icon-btn edit-btn" onClick={() => handleEdit(insurer)} title="Edit Insurer" aria-label={`Edit ${insurer.insurerName}`}>
                                                                <Edit size={18} />
                                                            </button>}
                                                            {hasPrivilege(insurerPrivileges.deactivate) && <button className="icon-btn delete-btn" onClick={() => { setInsurerToDeactivate(insurer._id); setShowDeleteConfirm(true); }} title="Deactivate Insurer" aria-label={`Deactivate ${insurer.insurerName}`}>
                                                                <Ban size={18} />
                                                            </button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="insurer-empty-state">
                                                        <Building2 size={42} />
                                                        <h3>No Insurers Found</h3>
                                                        <p>Try changing the filters or add a new insurer.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {showDeleteConfirm && (
                            <div className="confirmation-modal-overlay">
                                <div className="confirmation-modal-content small">
                                    <h3>Confirm Deactivation</h3>
                                    <p>Are you sure you want to deactivate this insurer?</p>

                                    <div className="confirmation-modal-actions">
                                        <button className="confirm-btn" onClick={confirmDeactivate}>Yes</button>
                                        <button className="cancel-btn" onClick={cancelDeactivate}>No</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="insurer-pagination-row">
                            <div className="insurer-pagination-meta">
                                <span>
                                    Showing {filteredActive.length ? activeFirst + 1 : 0}-{Math.min(activeLast, filteredActive.length)} of {filteredActive.length}
                                </span>
                                <small>Filtered insurer records</small>
                            </div>

                            <div className="insurer-pagination-controls">
                                <Pagination
                                    totalItems={filteredActive.length}
                                    itemsPerPage={activeItemsPerPage}
                                    currentPage={activePage}
                                    onPageChange={setActivePage}
                                />
                            </div>

                            <div className="items-per-page">
                                <label>Items per page:</label>
                                <select
                                    value={activeItemsPerPage}
                                    onChange={(e) => {
                                        setActiveItemsPerPage(Number(e.target.value));
                                        setActivePage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                        </div>

                        {showPopup && (
                            <div className="popup-overlay">
                                <div className="popup">
                                    <button className="popup-close" onClick={resetForm} aria-label="Close insurer form">
                                        <X size={18} />
                                    </button>
                                    <h2>{editMode ? 'Edit Insurer' : 'Add Insurer'}</h2>
                                    <p className="popup-subtitle">Maintain insurer identity, category, logo, and GST details.</p>

                                    <form onSubmit={handleSubmit} className="insurer-form">
                                        <label>
                                            <span>Insurer Name</span>
                                            <input
                                                type="text"
                                                name="insurerName"
                                                placeholder="Enter insurer name"
                                                value={formData.insurerName}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </label>
                                        <label>
                                            <span>Short Name</span>
                                            <input
                                                type="text"
                                                name="shortName"
                                                placeholder="Short code or display name"
                                                value={formData.shortName}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </label>
                                        <label>
                                            <span>Category</span>
                                            <select
                                                name="categoryId"
                                                value={formData.categoryId}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat._id} value={cat._id}>
                                                        {cat.categoryName}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label>
                                            <span>GST Number</span>
                                            <input
                                                type="text"
                                                name="gstNumber"
                                                placeholder="GST Number"
                                                value={formData.gstNumber}
                                                onChange={handleInputChange}
                                            />
                                        </label>
                                        <label className="insurer-form-full">
                                            <span>Address</span>
                                            <textarea
                                                name="address"
                                                placeholder="Registered office address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows="3"
                                            />
                                        </label>
                                        <label className="insurer-form-full">
                                            <span>Logo</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                        {preview && (
                                            <div className="logo-preview-panel">
                                                <img src={preview} alt="Logo Preview" className="logo-preview" />
                                                <span>Logo preview</span>
                                            </div>
                                        )}

                                        <div className="popup-actions">
                                            <button type="submit" className="submit-btn-im">
                                                {editMode ? 'Update Insurer' : 'Save Insurer'}
                                            </button>
                                            <button type="button" className="popup-cancel-btn" onClick={resetForm}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {showInactivePopup && (
                            <div className="popup-overlay">
                                <div className="popup large">
                                    <button className="popup-close" onClick={() => setShowInactivePopup(false)} aria-label="Close inactive insurers">
                                        <X size={18} />
                                    </button>

                                    <h2>Inactive Insurers</h2>
                                    <p className="popup-subtitle">Review deactivated insurers and restore records when required.</p>

                                    <div className="inactive-filters">
                                        <input
                                            type="text"
                                            placeholder="Search by name or short name"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />

                                        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                                            <option value="">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.categoryName}
                                                </option>
                                            ))}
                                        </select>

                                        <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
                                            <option value="">Sort By</option>
                                            <option value="insurerName">Insurer Name</option>
                                            <option value="shortName">Short Name</option>
                                            <option value="categoryName">Category</option>
                                        </select>

                                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                            <option value="asc">ASC</option>
                                            <option value="desc">DESC</option>
                                        </select>

                                        <button className="restore-all-btn" onClick={restoreAll}>
                                            <ArchiveRestore size={15} /> Restore All
                                        </button>
                                    </div>

                                    <div className="inactive-table-scroll">
                                        <table className="inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Insurer</th>
                                                    <th>Short Name</th>
                                                    <th>Category</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredInactive.map((item) => (
                                                    <tr key={item._id}>
                                                        <td>{item.insurerName}</td>
                                                        <td>{item.shortName}</td>
                                                        <td>{getCategoryName(item)}</td>
                                                        <td>
                                                            <button className="activate-btn" onClick={() => handleRestore(item._id)}>
                                                                Restore
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showBulkErrorPopup && (
                            <div className="popup-overlay">
                                <div className="popup large">
                                    <button className="popup-close" onClick={() => setShowBulkErrorPopup(false)} aria-label="Close bulk upload errors">
                                        <X size={18} />
                                    </button>

                                    <h2>Bulk Upload Errors</h2>
                                    <p className="popup-subtitle">Rows below were skipped during import.</p>

                                    <div className="inactive-table-scroll">
                                        <table className="inactive-table">
                                            <thead>
                                                <tr>
                                                    <th>Excel Row</th>
                                                    <th>Error Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bulkErrors.map((err, idx) => (
                                                    <tr key={idx}>
                                                        <td>{err.row}</td>
                                                        <td>{err.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default InsuranceMaster;
